import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

/**
 * Base input styles following the design system
 */
const baseInputStyles = (theme) => css`
  width: 100%;
  padding: 1rem 1.25rem;
  font-family: ${theme.fonts.secondary};
  /* The root is 10px (src/index.css): 1.6rem is 16px, the iOS no-zoom floor
     (#245). This file used to size itself as if the root were 16px (#243). */
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.5;
  color: ${theme.colors.greyDark};
  background-color: ${theme.colors.white};
  border: 2px solid ${theme.colors.greyLight};
  border-radius: 12px;
  transition: ${theme.transitions.default};
  box-shadow: ${theme.shadows.sm};

  &::placeholder {
    color: ${theme.colors.grey};
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow:
      ${theme.shadows.sm},
      0 0 0 3px ${theme.colors.primary}20;
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: ${theme.colors.grey};
  }

  &:disabled {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.grey};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

/**
 * Filled variant: the hero contact form's look, folded in from the former
 * forms/FormInput (#243). No border, white field, brand-gold text.
 */
const filledStyles = (theme) => css`
  width: 100%;
  height: 50px;
  background: ${theme.colors.white};
  border-radius: 8px;
  border: none;
  margin-bottom: 10px;
  padding: 0 16px;
  ${theme.mixins.textP3};
  font-size: 1.6rem;
  color: ${theme.colors.secondary};
  transition: ${theme.transitions.fast};

  &::placeholder {
    font-weight: 500;
    color: ${theme.colors.secondary};
  }

  &:focus {
    outline: none;
    box-shadow: ${theme.shadows.outline};
  }
`;

/**
 * Normal focus styles (not error state)
 */
const normalFocusStyles = (theme) => css`
  &:focus {
    outline: none;
    border-color: ${theme.colors
      .secondary}; /* Use secondary color instead of primary */
    box-shadow:
      ${theme.shadows.sm},
      0 0 0 3px ${theme.colors.secondary}30; /* Lighter opacity for subtle effect */
  }
`;

/**
 * Error state styles
 */
const errorStyles = (theme) => css`
  border-color: ${theme.colors.error};

  &:focus {
    border-color: ${theme.colors.error};
    box-shadow:
      ${theme.shadows.sm},
      0 0 0 3px ${theme.colors.error}20;
  }
`;

/**
 * Success state styles
 */
const successStyles = (theme) => css`
  border-color: ${theme.colors.success};
`;

const StyledInput = styled.input`
  ${({ $variant, theme }) =>
    $variant === 'filled' ? filledStyles(theme) : baseInputStyles(theme)}

  /* Focus, error and success states belong to the outlined variant */
  ${({ $variant, hasError, theme }) =>
    $variant !== 'filled' && !hasError && normalFocusStyles(theme)}
  ${({ $variant, hasError, theme }) =>
    $variant !== 'filled' && hasError && errorStyles(theme)}
  ${({ $variant, hasSuccess, theme }) =>
    $variant !== 'filled' && hasSuccess && successStyles(theme)}
`;

/**
 * Input field container for consistent spacing
 */
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/**
 * Label component with consistent styling
 */
const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.greyDark};
  margin-bottom: 0.5rem;
  line-height: 1.4;

  .required {
    color: ${({ theme }) => theme.colors.error};
    margin-left: 0.25rem;
  }
`;

/**
 * Error message component
 */
const ErrorMessage = styled.span`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.3rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.error};
  margin-top: 0.5rem;
  line-height: 1.4;
`;

/**
 * Help text component
 */
const HelpText = styled.span`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.3rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey};
  margin-top: 0.5rem;
  line-height: 1.4;
`;

/**
 * Enhanced Input component with built-in validation display
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Input label text
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message to display
 * @param {string} props.helpText - Help text to display when no error
 * @param {boolean} props.showSuccess - Whether to show success styling
 * @param {string} props.id - Input ID (falls back to label-based ID)
 * @param {'outlined'|'filled'} props.variant - Outlined field (default) or the filled hero look
 * @param {Object} props.containerProps - Props to pass to the container div
 */
const Input = forwardRef(
  (
    {
      label,
      required = false,
      error,
      helpText,
      showSuccess = false,
      id,
      variant = 'outlined',
      containerProps = {},
      ...inputProps
    },
    ref,
  ) => {
    // Generate ID from label if not provided
    const inputId =
      id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    const hasError = !!error;

    return (
      <InputContainer {...containerProps}>
        {label && (
          <Label htmlFor={inputId}>
            {label}
            {required && <span className="required">*</span>}
          </Label>
        )}

        <StyledInput
          {...inputProps}
          ref={ref}
          id={inputId}
          $variant={variant}
          hasError={hasError}
          hasSuccess={showSuccess && !hasError}
          aria-invalid={hasError}
          aria-describedby={
            error
              ? `${inputId}-error`
              : helpText
                ? `${inputId}-help`
                : undefined
          }
        />

        {hasError && (
          <ErrorMessage id={`${inputId}-error`} role="alert" aria-live="polite">
            {error}
          </ErrorMessage>
        )}

        {!hasError && helpText && (
          <HelpText id={`${inputId}-help`}>{helpText}</HelpText>
        )}
      </InputContainer>
    );
  },
);

Input.displayName = 'Input';

export default Input;
